import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { HelpContentService } from '../services/help-content.service';
import { HELP_CONTENT_BASE, HelpNode, ancestorIds } from '../help.models';
import { HelpTreeItemComponent } from '../help-tree-item/help-tree-item.component';

/** Pulls the topic id out of `/help/<id>`, or null anywhere else in the app. */
function topicIdFrom(url: string): string | null {
  const [path] = url.split(/[?#]/);
  const match = /^\/help\/([^/]+)$/.exec(path);

  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * The Contents tab: the help tree and nothing else.
 * Pages open in the working area through `/help/:topicId`, where there is room for them.
 */
@Component({
  selector: 'adf-help-toc',
  imports: [HelpTreeItemComponent],
  templateUrl: './help-toc.component.html',
  styleUrl: './help-toc.component.scss',
})
export class HelpTocComponent implements OnInit {
  private readonly _help = inject(HelpContentService);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);

  nodes: HelpNode[] = [];
  loading = true;
  failed = false;

  /** The sections to hold open so the current topic's row is actually visible. */
  openIds: string[] = [];

  /**
   * The whole help as one PDF, for reading offline or printing. A fixed file rather than something
   * generated per request: it is built by `npm run help:pdf` and committed alongside the pages it
   * is made from, so regenerate it whenever the content changes.
   */
  readonly pdfFileName = 'PreCalc-Help.pdf';
  readonly pdfHref = `${HELP_CONTENT_BASE}/${this.pdfFileName}`;

  /**
   * The topic the reader is showing, tracked from the router rather than from a click, so it is
   * right when someone arrives on `/help/<id>` by URL or by pressing Back.
   */
  private _openTopicId: string | null = topicIdFrom(this._router.url);

  ngOnInit(): void {
    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((event) => {
        this._openTopicId = topicIdFrom(event.urlAfterRedirects);
        this._updateOpenIds();
      });

    void this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.failed = false;

    try {
      const manifest = await this._help.loadManifest();
      this.nodes = manifest.nodes;
      this._updateOpenIds();
    } catch {
      this.failed = true;
    } finally {
      this.loading = false;
    }
  }

  private _updateOpenIds(): void {
    this.openIds = this._openTopicId ? ancestorIds(this.nodes, this._openTopicId) : [];
  }
}
